import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { Alert, Box, Button, Typography } from "@mui/material";
import useResponsive from "../../../hooks/useResponsive";
import { FixedSizeList } from "react-window";
import CellDetailPopover, { CellDetail } from "./CellDetailPopover";
import ColumnMenu from "./ColumnMenu";
import ExportMenu from "./ExportMenu";
import {
  autoFitWidth,
  frozenColumnCount,
  GridSelection,
  selectionRect,
} from "./gridGeometry";
import { selectedRowIds, summarizeSelection } from "./selectionSummary";
import SheetCards from "./SheetCards";
import SheetGrid from "./SheetGrid";
import { applyColumnOrder } from "./sheetPipeline";
import SheetToolbar from "./SheetToolbar";
import { ColumnId } from "./sheetTypes";
import { SortMode } from "./sheetViewState";
import { useSheetExport } from "./useSheetExport";
import { useSheetKeyboard } from "./useSheetKeyboard";
import { SheetLoader, useSheetView } from "./useSheetView";
import ViewStatusBar from "./ViewStatusBar";

/**
 * The spreadsheet viewer: toolbar, view status bar and grid over one workbook.
 * CsvRenderer and XlsxRenderer only parse their file and hand the sheets here,
 * so both formats behave identically from this point on.
 */

interface SheetViewProps {
  loaders: SheetLoader[];
  fileName: string;
  fileSizeLabel: string;
  sourceKey: string;
  stackIndex: number;
  headerSlot?: HTMLElement | null;
  onDownloadOriginal?: () => void;
  claimHeaderDownload?: (claimed: boolean) => void;
  getPopupContainer?: () => HTMLElement | null;
  /** Set when the parser stopped early; the export says so too. */
  truncatedRows?: number | null;
  truncationNote?: string | null;
}

const SheetView: React.FC<SheetViewProps> = ({
  loaders,
  fileName,
  fileSizeLabel,
  sourceKey,
  stackIndex,
  headerSlot,
  onDownloadOriginal,
  claimHeaderDownload,
  getPopupContainer,
  truncatedRows,
  truncationNote,
}) => {
  const api = useSheetView(loaders, sourceKey);
  const listRef = useRef<FixedSizeList | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [menuColumn, setMenuColumn] = useState<ColumnId | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [cellDetail, setCellDetail] = useState<CellDetail | null>(null);

  const { isCardMode } = useResponsive();
  const container = getPopupContainer ? getPopupContainer() : null;
  const popoverZIndex = 1300 + stackIndex + 1;

  // The header keeps a single download entry point, and it is this menu.
  useEffect(() => {
    if (!claimHeaderDownload) return;
    claimHeaderDownload(true);
    return () => claimHeaderDownload(false);
  }, [claimHeaderDownload]);

  const { view, source, state, activeMatch } = api;

  /**
   * The person's own choice wins; without one, the platform's own card-mode
   * breakpoint decides, so a phone never opens on a 34-column table.
   */
  const viewMode = state.viewMode ?? (isCardMode ? "cards" : "table");

  const emptyColumnIds = useMemo(
    () =>
      source.columns
        .filter((column) => api.types[column.id] === "empty")
        .map((column) => column.id),
    [source.columns, api.types],
  );

  /**
   * Every column in the order the person is looking at, hidden ones included.
   * The columns checklist lists it, the freeze block is measured against it and
   * the reorder actions carry it - the reducer holds ids only, so this is the
   * one place the order is worked out.
   */
  const orderedColumns = useMemo(
    () => applyColumnOrder(source, state.columnOrder),
    [source, state.columnOrder],
  );

  // Keep the highlighted match on screen; it moves when the view is re-ordered.
  useEffect(() => {
    if (activeMatch && listRef.current) {
      listRef.current.scrollToItem(activeMatch.rowPos, "center");
    }
  }, [activeMatch]);

  // A new set of rows starts at the top - otherwise a filter leaves the viewport
  // parked in the middle of rows that are no longer there. The active match is
  // read through a ref so that landing on a match does not re-trigger this.
  const activeMatchRef = useRef(activeMatch);
  useEffect(() => {
    activeMatchRef.current = activeMatch;
  }, [activeMatch]);

  const rowsIdentity = view.rows;
  useEffect(() => {
    if (!activeMatchRef.current && listRef.current) {
      listRef.current.scrollTo(0);
    }
  }, [rowsIdentity]);

  const selectionSummary = useMemo(
    () => summarizeSelection(view, api.selection),
    [view, api.selection],
  );
  const selectionRowIds = useMemo(
    () => selectedRowIds(view, api.selection),
    [view, api.selection],
  );
  const selectionColumnCount = useMemo(() => {
    if (!api.selection || api.selection.kind === "rows") {
      return view.columns.length;
    }
    const rect = selectionRect(api.selection);
    return Math.min(rect.right, view.columns.length - 1) - rect.left + 1;
  }, [api.selection, view.columns.length]);

  const setSelection = useCallback(
    (selection: GridSelection | null) => api.setSelection(selection),
    [api],
  );

  const handleSortColumn = useCallback(
    (columnId: ColumnId, additive: boolean) => {
      api.dispatch({
        type: "cycleSort",
        sheetId: api.activeSheetId,
        columnId,
        additive,
      });
    },
    [api],
  );

  const handleSortDirection = useCallback(
    (columnId: ColumnId, direction: "asc" | "desc" | null) => {
      if (direction === null) {
        const rest = state.sort.filter((rule) => rule.columnId !== columnId);
        api.dispatch({
          type: "setSort",
          sheetId: api.activeSheetId,
          rule: rest.length > 0 ? rest[0] : null,
        });
        return;
      }
      api.dispatch({
        type: "setSort",
        sheetId: api.activeSheetId,
        rule: { columnId, direction, mode: "auto" },
      });
    },
    [api, state.sort],
  );

  const handleSortMode = useCallback(
    (columnId: ColumnId, mode: SortMode) => {
      api.dispatch({
        type: "setSortMode",
        sheetId: api.activeSheetId,
        columnId,
        mode,
      });
    },
    [api],
  );

  const handleHideColumn = useCallback(
    (columnId: ColumnId) => {
      api.dispatch({
        type: "hideColumns",
        sheetId: api.activeSheetId,
        columnIds: [columnId],
      });
    },
    [api],
  );

  const handleHideEmptyColumns = useCallback(() => {
    if (emptyColumnIds.length === 0) return;
    api.dispatch({
      type: "hideColumns",
      sheetId: api.activeSheetId,
      columnIds: emptyColumnIds,
    });
  }, [api, emptyColumnIds]);

  const handleToggleColumn = useCallback(
    (columnId: ColumnId, visible: boolean) => {
      api.dispatch(
        visible
          ? {
              type: "showColumns",
              sheetId: api.activeSheetId,
              columnIds: [columnId],
            }
          : {
              type: "hideColumns",
              sheetId: api.activeSheetId,
              columnIds: [columnId],
            },
      );
    },
    [api],
  );

  const handleRevealColumnsBefore = useCallback(
    (columnId: ColumnId) => {
      const position = view.columns.findIndex((item) => item.id === columnId);
      if (position < 0) return;
      // The same list the marker counted, so the click can never bring back a
      // different set of columns than the one it offered.
      const ids = view.hiddenColumnsBefore[position];
      if (ids.length === 0) return;
      api.dispatch({
        type: "showColumns",
        sheetId: api.activeSheetId,
        columnIds: ids,
      });
    },
    [api, view],
  );

  const handleHideSelectedRows = useCallback(() => {
    if (selectionRowIds.length === 0) return;
    api.dispatch({
      type: "hideRows",
      sheetId: api.activeSheetId,
      rowIds: selectionRowIds,
    });
    api.setSelection(null);
  }, [api, selectionRowIds]);

  const handleKeepOnlySelectedRows = useCallback(() => {
    if (selectionRowIds.length === 0) return;
    api.dispatch({
      type: "keepOnlyRows",
      sheetId: api.activeSheetId,
      rowIds: selectionRowIds,
      allRowIds: view.rows.map((row) => row.sourceIndex),
    });
    api.setSelection(null);
  }, [api, selectionRowIds, view.rows]);

  const handleCellExpand = useCallback(
    (rowPos: number, colPos: number, anchor: HTMLElement) => {
      const row = view.rows[rowPos];
      const column = view.columns[colPos];
      if (!row || !column) return;
      const raw = row.cells[column.sourceIndex];
      setCellDetail({
        anchor,
        columnLabel: column.label
          ? `${column.letter} - ${column.label}`
          : column.letter,
        rowNumber: row.sourceIndex + 1,
        value: raw == null ? "" : raw,
      });
    },
    [view.rows, view.columns],
  );

  const frozenCount = useMemo(
    () =>
      frozenColumnCount(
        view.columns,
        state.frozenThroughSourceIndex,
        orderedColumns,
      ),
    [view.columns, state.frozenThroughSourceIndex, orderedColumns],
  );

  /**
   * One dispatch per drop: the reducer splices the full order and re-anchors
   * the freeze inside the same action, so a single undo puts both back.
   */
  const handleMoveColumn = useCallback(
    (columnId: ColumnId, beforeColumnId: ColumnId | null) => {
      api.dispatch({
        type: "moveColumn",
        sheetId: api.activeSheetId,
        columns: orderedColumns,
        columnId,
        beforeColumnId,
        frozenCount,
      });
    },
    [api, orderedColumns, frozenCount],
  );

  const handleResetColumnOrder = useCallback(() => {
    api.dispatch({
      type: "resetColumnOrder",
      sheetId: api.activeSheetId,
      columns: source.columns,
      frozenCount,
    });
  }, [api, source.columns, frozenCount]);

  const handleColumnWidthChange = useCallback(
    (columnId: ColumnId, width: number) => {
      api.dispatch({
        type: "setColumnWidth",
        sheetId: api.activeSheetId,
        columnId,
        width,
      });
    },
    [api],
  );

  const handleAutoFitColumn = useCallback(
    (columnId: ColumnId) => {
      const column = view.columns.find((item) => item.id === columnId);
      if (!column) return;
      api.dispatch({
        type: "setColumnWidth",
        sheetId: api.activeSheetId,
        columnId,
        width: autoFitWidth(view, column, column.label || column.letter),
      });
    },
    [api, view],
  );

  /**
   * The toolbar freezes the first visible column and unfreezes everything; the
   * column menu sets the boundary anywhere.
   */
  const handleToggleFreezeFirstColumn = useCallback(() => {
    const first = view.columns[0];
    api.dispatch({
      type: "setFrozenThrough",
      sheetId: api.activeSheetId,
      sourceIndex: frozenCount > 0 || !first ? null : first.sourceIndex,
    });
  }, [api, frozenCount, view.columns]);

  const handleFreezeThrough = useCallback(
    (columnId: ColumnId) => {
      const column = view.columns.find((item) => item.id === columnId);
      if (!column) return;
      api.dispatch({
        type: "setFrozenThrough",
        sheetId: api.activeSheetId,
        sourceIndex: column.sourceIndex,
      });
    },
    [api, view.columns],
  );

  const handleResetColumnWidths = useCallback(() => {
    Object.keys(state.columnWidths).forEach((columnId) => {
      api.dispatch({
        type: "setColumnWidth",
        sheetId: api.activeSheetId,
        columnId,
        width: null,
      });
    });
  }, [api, state.columnWidths]);

  const handleUnfreeze = useCallback(() => {
    api.dispatch({
      type: "setFrozenThrough",
      sheetId: api.activeSheetId,
      sourceIndex: null,
    });
  }, [api]);

  const handleRevealRows = useCallback(
    (rowIds: number[]) => {
      api.dispatch({ type: "showRows", sheetId: api.activeSheetId, rowIds });
    },
    [api],
  );

  const sheetLabel =
    loaders.length > 1
      ? (loaders.find((item) => item.id === api.activeSheetId)?.name ?? null)
      : null;

  const exportApi = useSheetExport({
    source,
    view,
    includeHeader: state.headerRowIndex != null,
    fileName,
    sheetLabel,
    sheetCount: loaders.length,
    buildAllSheetExports: api.buildAllSheetExports,
    selection: api.selection,
  });

  const rootRef = useRef<HTMLDivElement | null>(null);

  const handleEscape = useCallback((): boolean => {
    // A ladder, so Escape never closes the whole preview while something
    // smaller is still open. Menus and popovers close themselves.
    if (cellDetail) {
      setCellDetail(null);
      return true;
    }
    if (api.selection) {
      api.setSelection(null);
      return true;
    }
    if (api.query) {
      api.setQuery("");
      return true;
    }
    return false;
  }, [api, cellDetail]);

  const handleFocusSearch = useCallback(() => {
    searchInputRef.current?.focus();
    searchInputRef.current?.select();
  }, []);

  useSheetKeyboard(rootRef, {
    onUndo: api.undo,
    onRedo: api.redo,
    onCopySelection: exportApi.copySelection,
    onFocusSearch: handleFocusSearch,
    onEscape: handleEscape,
    hasSelection: api.selection != null,
  });

  const exportMenu = (
    <ExportMenu
      fileName={fileName}
      fileSizeLabel={fileSizeLabel}
      rowCount={view.rows.length}
      columnCount={view.columns.length}
      truncatedRows={truncatedRows ?? null}
      busy={exportApi.exporting}
      onDownloadOriginal={onDownloadOriginal ?? (() => undefined)}
      onExportView={exportApi.exportCurrentView}
      onExportAllSheets={exportApi.exportWorkbook}
      onCopyView={exportApi.copyView}
      sheetCount={loaders.length}
      container={container}
      zIndex={popoverZIndex}
    />
  );

  const restored = api.restored;
  // The bar also carries Undo and Redo, so it stays while either is possible -
  // otherwise undoing the last change would take its own Redo button away.
  const showStatusBar =
    api.isModified || api.selection != null || api.canUndo || api.canRedo;

  return (
    <>
      {headerSlot ? ReactDOM.createPortal(exportMenu, headerSlot) : null}
      <Box
        ref={rootRef}
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <SheetToolbar
          sheetIds={api.sheetIds}
          sheetNames={api.sheetNames}
          activeSheetId={api.activeSheetId}
          onSheetChange={api.setActiveSheet}
          query={api.query}
          onQueryChange={api.setQuery}
          matchCount={api.matches.length}
          currentMatch={api.activeMatchIndex}
          matchesTruncated={api.matchesTruncated}
          onPrevMatch={api.gotoPrevMatch}
          onNextMatch={api.gotoNextMatch}
          source={source}
          state={state}
          orderedColumns={orderedColumns}
          emptyColumnIds={emptyColumnIds}
          onToggleColumn={handleToggleColumn}
          onMoveColumn={handleMoveColumn}
          onShowAllColumns={() =>
            api.dispatch({
              type: "showColumns",
              sheetId: api.activeSheetId,
              columnIds: "all",
            })
          }
          onHideEmptyColumns={handleHideEmptyColumns}
          frozenCount={frozenCount}
          onToggleFreezeFirstColumn={handleToggleFreezeFirstColumn}
          viewMode={viewMode}
          onViewModeChange={(mode) =>
            api.dispatch({
              type: "setViewMode",
              sheetId: api.activeSheetId,
              mode,
            })
          }
          headerRowIndex={state.headerRowIndex}
          onHeaderRowToggle={(useFirstRow) =>
            api.dispatch({
              type: "setHeaderRowIndex",
              sheetId: api.activeSheetId,
              index: useFirstRow ? 0 : null,
            })
          }
          container={container}
          zIndex={popoverZIndex}
          searchInputRef={searchInputRef}
        />

        {truncationNote && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            {truncationNote}
          </Alert>
        )}

        {restored && (
          <Alert
            severity="info"
            sx={{ borderRadius: 0 }}
            onClose={api.dismissRestored}
            action={
              <Button color="inherit" size="small" onClick={api.resetView}>
                Reset view
              </Button>
            }
          >
            Restored your saved view ({restored.filters} filter
            {restored.filters === 1 ? "" : "s"}, {restored.sorts} sort
            {restored.sorts === 1 ? "" : "s"}, {restored.hiddenColumns} hidden
            column{restored.hiddenColumns === 1 ? "" : "s"},{" "}
            {restored.hiddenRows} hidden row
            {restored.hiddenRows === 1 ? "" : "s"}
            {restored.columnsReordered ? ", columns reordered" : ""}).
          </Alert>
        )}

        {api.query &&
          api.matches.length === 0 &&
          (api.matchesOutsideView.rows > 0 ||
            api.matchesOutsideView.hiddenColumnRows > 0) && (
            <Alert severity="info" sx={{ borderRadius: 0 }}>
              No matches in the current view.
              {api.matchesOutsideView.rows > 0
                ? ` ${api.matchesOutsideView.rows.toLocaleString()} matching row${api.matchesOutsideView.rows === 1 ? " is" : "s are"} filtered out or hidden.`
                : ""}
              {api.matchesOutsideView.hiddenColumnRows > 0
                ? ` ${api.matchesOutsideView.hiddenColumnRows.toLocaleString()} match${api.matchesOutsideView.hiddenColumnRows === 1 ? " sits" : "es sit"} in hidden columns.`
                : ""}
            </Alert>
          )}

        {showStatusBar && (
          <ViewStatusBar
            source={source}
            view={view}
            state={state}
            summary={selectionSummary}
            selectionRows={selectionRowIds.length}
            selectionColumns={selectionColumnCount}
            selectionKind={api.selection ? api.selection.kind : null}
            onClearFilter={(columnId) =>
              api.dispatch({
                type: "setFilter",
                sheetId: api.activeSheetId,
                columnId,
                filter: null,
              })
            }
            onClearSort={() =>
              api.dispatch({ type: "clearSort", sheetId: api.activeSheetId })
            }
            onShowAllColumns={() =>
              api.dispatch({
                type: "showColumns",
                sheetId: api.activeSheetId,
                columnIds: "all",
              })
            }
            onShowAllRows={() =>
              api.dispatch({
                type: "showRows",
                sheetId: api.activeSheetId,
                rowIds: "all",
              })
            }
            onUnfreeze={handleUnfreeze}
            onResetColumnWidths={handleResetColumnWidths}
            onResetColumnOrder={handleResetColumnOrder}
            frozenCount={frozenCount}
            onUndo={api.undo}
            onRedo={api.redo}
            canUndo={api.canUndo}
            canRedo={api.canRedo}
            onReset={api.resetView}
            onHideSelectedRows={handleHideSelectedRows}
            onKeepOnlySelectedRows={handleKeepOnlySelectedRows}
            onCopySelection={exportApi.copySelection}
            onClearSelection={() => api.setSelection(null)}
          />
        )}

        {source.columns.length === 0 ? (
          <EmptyState message="This sheet is empty." actionLabel={null} />
        ) : view.columns.length === 0 ? (
          <EmptyState
            message="All columns are hidden."
            actionLabel="Show all columns"
            onAction={() =>
              api.dispatch({
                type: "showColumns",
                sheetId: api.activeSheetId,
                columnIds: "all",
              })
            }
          />
        ) : source.rows.length === 0 ? (
          <EmptyState
            // A sheet whose only row became the header still has that row, and
            // the toggle is the way back to it - so the message points at it.
            message={
              state.headerRowIndex != null
                ? "No data rows - row 1 is used as the header. Turn that off under Columns to see it."
                : "This sheet has no data rows."
            }
            actionLabel={null}
          />
        ) : view.rows.length === 0 ? (
          <EmptyState
            message={
              state.hiddenRows.length > 0 && view.stats.filteredOutRows === 0
                ? "All rows are hidden."
                : "No rows match the current filters."
            }
            actionLabel={
              state.hiddenRows.length > 0 && view.stats.filteredOutRows === 0
                ? "Show all rows"
                : "Clear filters"
            }
            onAction={() =>
              api.dispatch(
                state.hiddenRows.length > 0 && view.stats.filteredOutRows === 0
                  ? {
                      type: "showRows",
                      sheetId: api.activeSheetId,
                      rowIds: "all",
                    }
                  : { type: "clearFilters", sheetId: api.activeSheetId },
              )
            }
          />
        ) : viewMode === "cards" ? (
          <SheetCards
            view={view}
            query={api.query}
            activeMatch={
              activeMatch
                ? { rowPos: activeMatch.rowPos, colPos: activeMatch.colPos }
                : null
            }
            selection={api.selection}
            listRef={listRef}
            onSelectionChange={setSelection}
            onCellExpand={handleCellExpand}
          />
        ) : (
          <SheetGrid
            view={view}
            widthBySourceIndex={api.widthBySourceIndex}
            hiddenRowIds={state.hiddenRows}
            showRowGapMarkers={state.sort.length === 0}
            query={api.query}
            activeMatch={
              activeMatch
                ? { rowPos: activeMatch.rowPos, colPos: activeMatch.colPos }
                : null
            }
            selection={api.selection}
            sort={state.sort}
            listRef={listRef}
            onSelectionChange={setSelection}
            onSortColumn={handleSortColumn}
            onOpenColumnMenu={(columnId, anchor) => {
              setMenuColumn(columnId);
              setMenuAnchor(anchor);
            }}
            onRevealRows={handleRevealRows}
            onRevealColumnsBefore={handleRevealColumnsBefore}
            onCellExpand={handleCellExpand}
            columnWidths={state.columnWidths}
            frozenCount={frozenCount}
            onColumnWidthChange={handleColumnWidthChange}
            onAutoFitColumn={handleAutoFitColumn}
            onMoveColumn={handleMoveColumn}
          />
        )}

        <CellDetailPopover
          detail={cellDetail}
          container={container}
          zIndex={popoverZIndex}
          onClose={() => setCellDetail(null)}
          onCopy={exportApi.copyValue}
        />

        <ColumnMenu
          key={menuColumn ?? "none"}
          open={!!menuColumn}
          anchorEl={menuAnchor}
          container={container}
          zIndex={popoverZIndex}
          columnId={menuColumn}
          source={source}
          view={view}
          state={state}
          types={api.types}
          emptyColumnCount={emptyColumnIds.length}
          onClose={() => setMenuColumn(null)}
          onApplyFilter={(columnId, filter) =>
            api.dispatch({
              type: "setFilter",
              sheetId: api.activeSheetId,
              columnId,
              filter,
            })
          }
          onSort={handleSortDirection}
          onSortMode={handleSortMode}
          onHideColumn={handleHideColumn}
          onHideEmptyColumns={handleHideEmptyColumns}
          onFreezeThrough={handleFreezeThrough}
          onUnfreeze={handleUnfreeze}
          isFrozen={
            menuColumn != null &&
            frozenCount > 0 &&
            view.columns.findIndex((item) => item.id === menuColumn) <
              frozenCount
          }
        />
      </Box>
    </>
  );
};

const EmptyState: React.FC<{
  message: string;
  actionLabel: string | null;
  onAction?: () => void;
}> = ({ message, actionLabel, onAction }) => (
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      p: 4,
      backgroundColor: "background.paper",
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
    {actionLabel && onAction && (
      <Button size="small" onClick={onAction} sx={{ textTransform: "none" }}>
        {actionLabel}
      </Button>
    )}
  </Box>
);

export default SheetView;
